export type FaqQA = { q: string; a: string };
export type FaqGroup = { id: string; title: string; items: FaqQA[] };

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: "platform",
    title: "Platform and Methodology",
    items: [
      {
        q: "What is XAU Pro Academy?",
        a: "XAU Pro Academy is an educational and quantitative research platform focused on XAUUSD (gold). It combines market structure research, public data tools (COT, CPI, NFP, seasonality, daily range and ATR), calculators, an education library, and the output of a systematic decision engine. It is not a broker and does not manage client funds.",
      },
      {
        q: "What is Smart Money Concepts (SMC) and why does the platform use it?",
        a: "SMC is a price action framework built around market structure, liquidity, displacement, and qualified points of interest such as Order Blocks and Fair Value Gaps. It is used because every reading has a defined structure and a logical invalidation level, which keeps risk measurable. Retail indicators such as RSI or MACD are not primary inputs.",
      },
      {
        q: "How do Orion AI, OTAS-01, and OSAS-01 relate to each other?",
        a: "Orion AI is the umbrella system: the supervising intelligence that monitors market conditions, enforces news protection, and applies capital preservation rules. It does not execute trades itself. It operates two quantitative execution models. OTAS-01 is the tactical intraday model, publishing XAUUSD setups inside defined London and New York session windows. OSAS-01 is the strategic model, publishing rare multi-day swing setups on the higher timeframe structure. Both models appear side by side in the XAU Terminal™, and every alert is labelled with the model that produced it. One system, one record, two specialized engines.",
      },
      {
        q: "What is OTAS-01?",
        a: "OTAS-01 is the tactical decision engine under Orion AI. It publishes intraday XAUUSD setups when fixed, rules based criteria are satisfied: higher timeframe trend context, session windows, news protection, and risk constraints. Setups that do not pass every condition are rejected. Its outputs are automated model results, not personalised human advice.",
      },
      {
        q: "What is OSAS-01?",
        a: "OSAS-01 (Orion Strategic Allocation System) is the strategic swing model under Orion AI. Where OTAS-01 works the intraday sessions, OSAS-01 operates on the H4 timeframe and holds positions across multiple days, including weekends when the setup requires it. Its setups are rarer and are graded A+ only: every strategic trade must show full higher timeframe alignment, liquidity interaction, and displacement before it qualifies. Because of that strict qualification, OSAS-01 may publish zero to one setup in a given period.",
      },
      {
        q: "What is the difference between OTAS-01 and StructureLens?",
        a: "OTAS-01 scans the market on its own and publishes decisions when its rules are satisfied. StructureLens analyses a chart you upload and returns a structured SMC reading (bias, structure, liquidity, POI, risk). StructureLens does not publish signals.",
      },
    ],
  },
  {
    id: "signals",
    title: "Signals and Execution",
    items: [
      {
        q: "How many setups are published per week?",
        a: "Frequency depends on market conditions. The engine only publishes setups that pass every criterion, so a week with zero or one decision is normal and is reported as it is. No setup is forced to maintain frequency.",
      },
      {
        q: "Are the setups intraday or swing trades?",
        a: "Both, published by two separate engines under Orion AI. OTAS-01 (tactical) opens intraday positions inside defined session windows, mainly around London and New York liquidity, and manages them to pre-defined levels within the session. OSAS-01 (strategic) publishes rarer H4 swing setups that are held across multiple days and may carry through weekends. Each published setup states its model and horizon, so the two are never confused. Neither is retail scalping.",
      },
      {
        q: "What do the A+ and A grades mean?",
        a: "Grades describe setup quality. A+ setups meet every confluence condition and may use an extended final target. A setups are valid but carry a minor concern, so they follow stricter allocation rules. Lower quality readings are rejected and never published. Every OSAS-01 strategic swing setup is graded A+ automatically, because a multi-day position must satisfy the full alignment standard before it qualifies at all. OTAS-01 intraday setups are graded A+ or A depending on confluence depth.",
      },
      {
        q: "How is each position managed after entry?",
        a: "Every position has a fixed stop and pre-defined targets published at entry. Part of the position is secured at the first target and the remaining risk moves to breakeven, so a winning trade cannot become a full loss after that point.",
      },
      {
        q: "How are setups delivered?",
        a: "Published decisions appear in the member XAU Terminal™ as soon as they are issued, with browser notifications. Every account, free or paid, can also connect a private Telegram bot that sends alerts by direct message. Paid members receive every A and A+ setup with full entry, stop and target levels in real time. Free accounts receive two full showcase A setups per month, plus withheld previews of every other setup and its verified outcome, so the record is visible before upgrading.",
      },
      {
        q: "Which broker or platform do I need?",
        a: "Any regulated broker offering XAUUSD with tight spreads during London and New York hours. Levels can be executed on MT4, MT5, cTrader, TradingView, or any platform that accepts market orders. Spreads and slippage around news releases differ between brokers.",
      },
    ],
  },
  {
    id: "risk",
    title: "Risk and Performance",
    items: [
      {
        q: "How is risk defined?",
        a: "Every published decision risks a fixed 1R, where 1R is the distance from entry to stop. You size the position to your own account using the Position Size Calculator, typically 0.5% to 1% of account equity per trade.",
      },
      {
        q: "How is drawdown managed?",
        a: "Risk is capped per position, exposure is limited while another position is still at risk, and signal generation pauses around high impact releases (FOMC, CPI, NFP) and in low liquidity windows. Across the verified 2024 to 2026 record the maximum drawdown was 8.25R.",
      },
      {
        q: "Where can I verify the performance?",
        a: "The Track Record page publishes the audited monthly results in R, and every closed trade is archived on the Case Studies page with entry, exit, and a candle replay. Losses are published alongside wins. Past performance does not guarantee future results.",
      },
      {
        q: "Is the platform compatible with prop firm challenges?",
        a: "Yes. Fixed 1R risk, defined invalidation, and news pauses fit daily and maximum drawdown rules. Check each firm's rules on news trading and holding positions, and size positions so a series of losses stays inside your daily limit.",
      },
    ],
  },
  {
    id: "membership",
    title: "Membership and Account",
    items: [
      {
        q: "What does a free account include?",
        a: "A free account includes the public tools and calculators, the education library, one StructureLens analysis per week, and access to the free dashboard. No card is required and it does not expire.",
      },
      {
        q: "What paid plans are available?",
        a: "Paid membership is offered monthly or annually only. There are no quarterly or lifetime plans. Paid enrolment currently operates through a waitlist, which you can join from the Membership page.",
      },
      {
        q: "Can I cancel a paid membership?",
        a: "Yes. You can cancel from your account at any time and keep access until the end of the current billing period.",
      },
    ],
  },
  {
    id: "legal",
    title: "Legal",
    items: [
      {
        q: "Is this financial advice?",
        a: "No. All content, alerts, and case studies are educational and generated by automated models. They are not personalised investment advice. Trading leveraged gold carries a high risk of loss, and every trading decision remains your responsibility.",
      },
      {
        q: "Who operates the platform?",
        a: "XAU Pro Academy is operated by Orion AI Technologies (License No. 58070). The platform does not receive compensation from brokers or prop firms referenced on the site.",
      },
    ],
  },
];
